import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { parseQueryParams } from '@lwtears/lwt-common-frontend/lib/@common/util/query-params-util';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { AssessmentType, Domain, Maybe, UnitAssessment } from 'types';
import { getCurriculumBenchmarkAssessments, getFormattedDate } from 'util/assessments-util';
import useStudentAssessmentsSummaryStore from 'graph/state/query-hooks/use-student-assessment-summary';
import AssessmentScore from 'components/common/AssessmentScore';
import OutlineButton from 'components/common/Buttons/OutlineButton';
import AssessmentScoreLegend from 'components/common/AssessmentScoreLegend';
import clsx from 'clsx';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    pageHeader: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
    },
    pt2: {
      paddingTop: theme.spacing(2),
    },
    pb2: {
      paddingBottom: theme.spacing(2),
    },
    untestedDomain: {
      backgroundColor: (theme.palette as any).grey[500],
    },
    studentList: {
      marginBottom: theme.spacing(4),
      '&.MuiTableContainer-root': {
        backgroundColor: 'white',
      },
      '& .MuiTableCell-root': {
        border: `1px solid ${(theme.palette as any).grey[600]}`,
        padding: theme.spacing(2),
        color: theme.palette.black.main,
        fontWeight: '600',
      },
      '& .MuiTableCell-head': {
        textAlign: 'center',
      },
    },
    sectionHeader: {
      color: theme.palette.primary.main,
    },
    secondaryLink: {
      textDecoration: 'none !important',
      color: theme.palette.black.main + ' !important',
      marginLeft: theme.spacing(4),
      fontFamily: 'HWT Futura',
      '&:hover': {
        textDecoration: 'underline !important',
      },
    },
    dFlex: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
  };
});

const StudentSummary = () => {
  const navigate = useNavigate();
  const query = parseQueryParams();
  const classes = useStyles();
  const reference = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => reference.current,
    onBeforeGetContent: () => {
      return Promise.resolve();
    },
  });

  if (
    query === null ||
    query.studentId === null ||
    query.curriculumId === null ||
    query.classId === null ||
    isNaN(query.studentId) ||
    isNaN(query.curriculumId) ||
    isNaN(query.classId)
  ) {
    navigate('/404');
    return null;
  }

  const [summary] = useStudentAssessmentsSummaryStore(
    parseInt(query.studentId),
    parseInt(query.curriculumId)
  );

  if (summary.isLoading) {
    return <></>;
  } else if (
    summary.student == null ||
    summary.productCurriculum == null ||
    summary.assessments === undefined ||
    summary.assessments?.length === 0 ||
    summary.results === undefined
  ) {
    navigate('/404');
    return null;
  }

  const curriculumId = Number(query.curriculumId);

  const unitAssessments =
    summary.assessments
      .filter((assessment) => assessment.type === AssessmentType.Unit)
      .map((assessment) => assessment as UnitAssessment) || [];

  const unitAssessmentResults = summary.results.filter(
    (result) => unitAssessments.find((ua) => ua.id === result.assessmentId) !== undefined
  );

  const unitAssessmentsDomainScores = [
    ...new Map(
      Array.from(unitAssessments.map((ua) => Array.from(ua.assessmentDomains.map((d) => d.domain))))
        .flat()
        .filter((val) => val != null)
        .map((domain) => [domain.id, domain])
    ).values(),
  ].map((domain) => {
    const domainScoreAccumulation: {
      domain: Domain;
      scores: Maybe<number>[];
    } = {
      domain: domain,
      scores: [],
    };

    unitAssessments.forEach((ua) => {
      const domainIndex = ua.assessmentDomains.findIndex(
        (assessmentDomain) => assessmentDomain.domain.id === domain.id
      );

      if (domainIndex === -1) {
        domainScoreAccumulation.scores.push(-1);
      } else {
        domainScoreAccumulation.scores.push(
          unitAssessmentResults.find((uaResult) => uaResult.assessmentId === ua.id)
            ?.assessmentDomains[domainIndex].stats.score || 0
        );
      }
    });

    return domainScoreAccumulation;
  });

  const benchmarkAssessments = getCurriculumBenchmarkAssessments(summary.assessments, curriculumId);

  const benchmarkAssessmentResults = summary.results.filter(
    (result) => benchmarkAssessments.find((ba) => ba.id === result.assessmentId) !== undefined
  );

  const benchmarkAssessmentsDomainScores = [
    ...new Map(
      Array.from(
        benchmarkAssessments.map((ba) => Array.from(ba.assessmentDomains.map((d) => d.domain)))
      )
        .flat()
        .filter((val) => val != null)
        .map((domain) => [domain.id, domain])
    ).values(),
  ].map((domain) => {
    const domainScoreAccumulation: {
      domain: Domain;
      scores: (Maybe<number> | undefined)[];
    } = {
      domain: domain,
      scores: [],
    };

    benchmarkAssessments.forEach((ba) => {
      const domainIndex = ba.assessmentDomains.findIndex(
        (assessmentDomain) => assessmentDomain.domain.id === domain.id
      );

      if (domainIndex === -1) {
        domainScoreAccumulation.scores.push(-1);
      } else {
        domainScoreAccumulation.scores.push(
          benchmarkAssessmentResults.find((baResult) => baResult.assessmentId === ba.id)
            ?.assessmentDomains[domainIndex].stats.score || 0
        );
      }
    });

    return domainScoreAccumulation;
  });

  return (
    <div className={classes.mainContainer} style={{ flexGrow: 1 }}>
      <Grid container item xs={12} className={classes.pageHeader}>
        <Grid item xs={9}>
          <Typography variant="h1">{t('assessmentSummary.title')}</Typography>
        </Grid>
        <Grid item xs={3} className={classes.dFlex}>
          <OutlineButton onClick={handlePrint}>{t('assessmentSummary.printNow')}</OutlineButton>
          <Link
            className={classes.secondaryLink}
            to={`/class/${query.classId}?tabSelection=${query.returnTab || 'Benchmark'}`}
          >
            <Typography variant="body1">{t('assessmentSummary.returnToClass')}</Typography>
          </Link>
        </Grid>
      </Grid>
      <Divider />
      <div ref={reference}>
        {/** BEGIN PRINTABLE DOCUMENT SECTION */}
        {/** PAGE 1. STUDENT INFO / UNITS INFO. */}
        <Grid item container xs={12}>
          <Grid item xs={8}>
            <Typography component="h2" variant="h2">
              {summary.productCurriculum.name}
            </Typography>
            <Typography component="p" variant="body1" className={classes.pb2}>
              {summary.productCurriculum.code}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography component="p" variant="body1" align="right">
              {`${t('assessmentSummary.studentName')}: ${summary.student.firstName} ${
                summary.student.lastName
              }`}
            </Typography>
            <Typography component="p" variant="body1" align="right" className={classes.pt2}>
              {`${t('assessmentSummary.printedOn')}: ${getFormattedDate(new Date())}`}
            </Typography>
          </Grid>
        </Grid>
        <Grid container item xs={12} justifyContent="center">
          <AssessmentScoreLegend />
        </Grid>
        {unitAssessments.length > 0 && (
          <>
            <Divider />
            <Typography
              component="p"
              variant="h2"
              className={clsx(classes.sectionHeader, classes.pb2)}
            >
              {t('assessmentSummary.unitSummary')}
            </Typography>
            <TableContainer className={classes.studentList}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {unitAssessments.map((assessment) => (
                      <TableCell key={'unit-head-' + assessment.id}>
                        {`${t('class.unit')} ${assessment.unit.unitNumber}`}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unitAssessmentsDomainScores.map((domain) => (
                    <TableRow key={'unit-domain-' + domain.domain.code}>
                      <TableCell width="33%">{domain.domain.title}</TableCell>
                      {domain.scores.map((score, index) => (
                        <TableCell
                          key={'unit-domain-score-' + domain.domain.code + '-' + index}
                          className={score === -1 ? classes.untestedDomain : ''}
                        >
                          <Grid container alignContent="center" justifyContent="center">
                            {score !== -1 && <AssessmentScore showLabel={false} score={score} />}
                          </Grid>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid item container xs={12}>
              {unitAssessments
                .map((assessment) =>
                  unitAssessmentResults.find((uar) => uar.assessmentId === assessment.id)
                )
                .filter((result) => result?.comments != null && result.comments.trim().length > 0)
                .map((result) => (
                  <Grid item xs={12} key={'unit-score-commands-' + result?.id}>
                    <div style={{ display: 'flex' }}>
                      <Typography component="p" variant="h4" className={classes.pb2}>
                        {`${t('assessmentSummary.comments')} -`}&nbsp;
                      </Typography>
                      <Typography
                        component="p"
                        variant="h4"
                        className={classes.pb2}
                        style={{ textDecoration: 'underline' }}
                      >
                        {`${t('class.unit')} ${
                          unitAssessments.find((ua) => ua.id === result?.assessmentId)?.unit
                            .unitNumber
                        }`}
                      </Typography>
                    </div>
                    <Typography component="p" variant="body1" className={classes.pb2}>
                      {result?.comments}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          </>
        )}
        {/** BENCHMARKS INFO. */}
        {benchmarkAssessments.length && (
          <>
            <Divider />
            <Typography
              component="p"
              variant="h2"
              className={clsx(classes.sectionHeader, classes.pb2)}
            >
              {t('assessmentSummary.benchmarkSummary')}
            </Typography>
            {/** TODO: Check column count for table */}
            <TableContainer className={classes.studentList}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {benchmarkAssessments.map((assessment) => (
                      <TableCell key={'benchmark-head-' + assessment.id}>
                        {`${t('class.benchmarkPeriod-' + assessment.period)}`}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {benchmarkAssessmentsDomainScores.map((domain) => (
                    <TableRow key={'benchmark-domain-' + domain.domain.code}>
                      <TableCell width="33%">{domain.domain.title}</TableCell>
                      {domain.scores.map((score, index) => (
                        <TableCell
                          key={'benchmark-domain-score-' + domain.domain.code + '-' + index}
                          className={score === -1 ? classes.untestedDomain : ''}
                        >
                          <Grid container alignContent="center" justifyContent="center">
                            {score !== -1 && <AssessmentScore showLabel={false} score={score} />}
                          </Grid>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid item container xs={12}>
              {benchmarkAssessments
                .map((assessment) =>
                  benchmarkAssessmentResults.find((bar) => bar.assessmentId === assessment.id)
                )
                .filter((result) => result?.comments != null && result.comments.trim().length > 0)
                .map((result) => (
                  <Grid item xs={12} key={'benchmark-score-commands-' + result?.id}>
                    <div style={{ display: 'flex' }}>
                      <Typography component="p" variant="h4" className={classes.pb2}>
                        {`${t('assessmentSummary.comments')} -`}&nbsp;
                      </Typography>
                      <Typography
                        component="p"
                        variant="h4"
                        className={classes.pb2}
                        style={{ textDecoration: 'underline' }}
                      >
                        {`${t(
                          'class.benchmarkPeriod-' +
                            benchmarkAssessments.find((ba) => ba.id === result?.assessmentId)
                              ?.period
                        )} `}
                      </Typography>
                    </div>
                    <Typography component="p" variant="body1" className={classes.pb2}>
                      {result?.comments}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          </>
        )}
        {/** END PRINTABLE DOCUMENT SECTION */}
      </div>
    </div>
  );
};

export default StudentSummary;
